<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';

const TIPOS = [
  { value: 'wame', label: 'wa.me / link genérico' },
  { value: 'ad', label: 'Click-to-WhatsApp Ads (Meta)' },
  { value: 'qrcode', label: 'QR Code físico' },
  { value: 'outro', label: 'Outro' },
];

export default {
  name: 'OrigensCaptacao',
  components: { Layout, PageHeader },
  data() {
    return {
      origens: [],
      etiquetas: [],
      estagios: [],
      carregando: true,
      tipos: TIPOS,
      editando: null,
      form: this.formVazio(),
      salvando: false,
      erro: null,
      sucesso: null,
    };
  },
  async mounted() {
    await Promise.all([this.fetchOrigens(), this.fetchEtiquetas(), this.fetchEstagios()]);
  },
  methods: {
    formVazio() {
      return {
        id: null,
        nome: '',
        codigo: '',
        tipo: 'wame',
        source_id: '',
        prefix_wame: '',
        rotulo_id: null,
        estagio_inicial_id: null,
        telefone_destino: '',
        mensagem_padrao: '',
        ativo: true,
      };
    },
    async fetchOrigens() {
      this.carregando = true;
      try {
        const { data } = await axios.get('/origem/captacao', {
          params: {
            include: JSON.stringify([
              { model: 'Rotulo', as: 'rotulo' },
              { model: 'ConversaEstagio', as: 'estagioInicial' },
            ]),
            order: JSON.stringify([['created_at', 'desc']]),
          },
        });
        this.origens = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao listar origens: ${error.message}`;
      } finally {
        this.carregando = false;
      }
    },
    async fetchEtiquetas() {
      try {
        const { data } = await axios.get('/rotulo', { params: { limit: 200, order: JSON.stringify([['nome', 'asc']]) } });
        this.etiquetas = Array.isArray(data) ? data : [];
      } catch (_e) { this.etiquetas = []; }
    },
    async fetchEstagios() {
      try {
        const { data } = await axios.get('/conversa/estagio', {
          params: { where: JSON.stringify({ status: true }), order: JSON.stringify([['ordem', 'asc']]) },
        });
        this.estagios = Array.isArray(data) ? data : [];
      } catch (_e) { this.estagios = []; }
    },
    iniciarNovo() {
      this.editando = 'novo';
      this.form = this.formVazio();
      this.erro = null;
      this.sucesso = null;
    },
    iniciarEdicao(o) {
      this.editando = o.id;
      this.form = {
        id: o.id, nome: o.nome, codigo: o.codigo, tipo: o.tipo,
        source_id: o.source_id || '', prefix_wame: o.prefix_wame || '',
        rotulo_id: o.rotulo_id || null, estagio_inicial_id: o.estagio_inicial_id || null,
        telefone_destino: o.telefone_destino || '', mensagem_padrao: o.mensagem_padrao || '',
        ativo: Boolean(o.ativo),
      };
      this.erro = null;
      this.sucesso = null;
    },
    cancelar() {
      this.editando = null;
      this.form = this.formVazio();
    },
    async salvar() {
      this.salvando = true;
      this.erro = null;
      try {
        const payload = { ...this.form };
        if (!payload.nome || !payload.codigo) {
          this.erro = 'Nome e código são obrigatórios.';
          this.salvando = false;
          return;
        }
        if (this.editando === 'novo') {
          await axios.post('/origem/captacao', payload);
          this.sucesso = 'Origem cadastrada.';
        } else {
          await axios.put(`/origem/captacao/${payload.id}`, payload);
          this.sucesso = 'Origem atualizada.';
        }
        this.cancelar();
        await this.fetchOrigens();
      } catch (error) {
        this.erro = (error.response && error.response.data && error.response.data.error) || error.message;
      } finally {
        this.salvando = false;
      }
    },
    async excluir(o) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Excluir origem "${o.nome}"?`)) return;
      try {
        await axios.delete(`/origem/captacao/${o.id}`);
        await this.fetchOrigens();
      } catch (error) {
        this.erro = `Falha ao excluir: ${error.message}`;
      }
    },
    gerarLink(o) {
      const tel = (o.telefone_destino || '').replace(/\D/g, '');
      if (!tel) return null;
      const partes = [o.prefix_wame, o.mensagem_padrao].filter(Boolean).join(' ').trim();
      const params = partes ? `?text=${encodeURIComponent(partes)}` : '';
      return `https://wa.me/${tel}${params}`;
    },
    async copiarLink(o) {
      const link = this.gerarLink(o);
      if (!link) {
        this.erro = 'Configure o telefone de destino para gerar o link.';
        return;
      }
      try {
        await navigator.clipboard.writeText(link);
        this.sucesso = 'Link copiado!';
        setTimeout(() => { this.sucesso = null; }, 2500);
      } catch (_e) {
        // fallback se clipboard API bloqueada
        // eslint-disable-next-line no-alert
        window.prompt('Copie o link manualmente:', link);
      }
    },
    nomeTipo(t) { return (TIPOS.find((x) => x.value === t) || {}).label || t; },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Origens de captação" pageTitle="Cadastros" />

    <BCard no-body>
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="m-0">Campanhas de captação</h5>
          <small class="text-muted">Rastreie de onde vieram seus contatos. Aplica etiquetas automaticamente.</small>
        </div>
        <button v-if="!editando" type="button" class="btn btn-primary" @click="iniciarNovo">
          <i class="ri-add-line me-1"></i>Nova origem
        </button>
      </div>

      <div class="card-body">
        <div v-if="erro" class="alert alert-danger">
          <i class="ri-error-warning-line me-1"></i>{{ erro }}
        </div>
        <div v-if="sucesso" class="alert alert-success">
          <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
        </div>

        <!-- Formulário (novo ou edição) -->
        <div v-if="editando" class="border rounded p-3 mb-4 bg-light-subtle">
          <h6 class="mb-3">
            <i class="ri-edit-line me-1"></i>{{ editando === 'novo' ? 'Nova origem' : 'Editando origem' }}
          </h6>
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label small">Nome *</label>
              <input v-model="form.nome" type="text" class="form-control" placeholder="Ex: Filiação RJ 2026" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">Código único *</label>
              <input v-model="form.codigo" type="text" class="form-control" placeholder="pcdob-rj-2026" />
            </div>
            <div class="col-md-3">
              <label class="form-label small">Tipo</label>
              <select v-model="form.tipo" class="form-select">
                <option v-for="t in tipos" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>
            </div>

            <div v-if="form.tipo === 'ad'" class="col-md-6">
              <label class="form-label small">Source ID (Meta Ad ID)</label>
              <input v-model="form.source_id" type="text" class="form-control" placeholder="ID do anúncio na Meta" />
              <small class="text-muted">Vem do referral.source_id no webhook do Click-to-WhatsApp.</small>
            </div>

            <div v-if="form.tipo !== 'ad'" class="col-md-6">
              <label class="form-label small">Prefixo (aparece no início da mensagem)</label>
              <input v-model="form.prefix_wame" type="text" class="form-control" placeholder="[#pcdob-rj-2026]" />
              <small class="text-muted">Identifica origem em links wa.me. Será removido da mensagem antes de salvar.</small>
            </div>

            <div class="col-md-3">
              <label class="form-label small">Etiqueta a aplicar</label>
              <select v-model="form.rotulo_id" class="form-select">
                <option :value="null">— nenhuma —</option>
                <option v-for="e in etiquetas" :key="e.id" :value="e.id">{{ e.nome }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small">Estágio inicial (opcional)</label>
              <select v-model="form.estagio_inicial_id" class="form-select">
                <option :value="null">— Caixa de entrada (default) —</option>
                <option v-for="e in estagios" :key="e.id" :value="e.id">{{ e.nome }}</option>
              </select>
            </div>

            <div v-if="form.tipo !== 'ad'" class="col-md-4">
              <label class="form-label small">Telefone destino (wa.me)</label>
              <input v-model="form.telefone_destino" type="text" class="form-control" placeholder="553140422118" />
            </div>
            <div v-if="form.tipo !== 'ad'" class="col-md-8">
              <label class="form-label small">Mensagem padrão (texto pré-preenchido)</label>
              <input v-model="form.mensagem_padrao" type="text" class="form-control" placeholder="Olá, quero saber mais sobre o partido!" />
            </div>

            <div class="col-12">
              <div class="form-check form-switch">
                <input id="ativo" v-model="form.ativo" type="checkbox" class="form-check-input" />
                <label class="form-check-label" for="ativo">Ativo</label>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button type="button" class="btn btn-primary btn-sm" :disabled="salvando" @click="salvar">
              <i class="ri-save-line me-1"></i>{{ salvando ? 'Salvando...' : 'Salvar' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelar">Cancelar</button>
          </div>
        </div>

        <!-- Lista -->
        <div v-if="carregando" class="text-muted text-center py-3">Carregando...</div>
        <div v-else-if="!origens.length" class="text-center text-muted py-5">
          <i class="ri-funnel-line" style="font-size: 2rem;"></i>
          <div class="mt-2">Nenhuma origem cadastrada. Crie uma para começar a rastrear de onde vêm seus contatos.</div>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Código</th>
                <th>Tipo</th>
                <th>Etiqueta</th>
                <th>Estágio</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="o in origens" :key="o.id">
                <td><strong>{{ o.nome }}</strong></td>
                <td><code class="small">{{ o.codigo }}</code></td>
                <td>{{ nomeTipo(o.tipo) }}</td>
                <td>{{ (o.rotulo && o.rotulo.nome) || '—' }}</td>
                <td>{{ (o.estagioInicial && o.estagioInicial.nome) || 'Caixa de entrada (default)' }}</td>
                <td>
                  <span class="badge" :class="o.ativo ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'">
                    {{ o.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="text-nowrap">
                  <button
                    v-if="o.tipo !== 'ad' && o.telefone_destino"
                    type="button"
                    class="btn btn-sm btn-outline-success me-1"
                    title="Gerar e copiar link wa.me"
                    @click="copiarLink(o)"
                  >
                    <i class="ri-link"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1" @click="iniciarEdicao(o)">
                    <i class="ri-edit-line"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="excluir(o)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </BCard>
  </Layout>
</template>
